import { cookiePrefixesToPickUp, SUBMIT_BUTTON_ID } from '@Types/consts.ts';
import { OnboardingFormFields, QueryParamsToStore } from '@Types/types.ts';
import { getCookiesList, getQueryParamsObject, isObjectEmpty } from '@Services/utils/utils.ts';
import { useBoundStore } from '@Store/index.ts';
import {
  applyColorScheme,
  dunAndBradstreetColorScheme,
  liliColorScheme,
} from '../colors/colors.ts';
import { EnvironmentVariables, getEnvironmentVariable } from '../env/environment.service.ts';
import { initTrackingService } from '@Services/analytics/tracking.service.ts';
import { initHotJar } from '@Services/analytics/hotjar.service.ts';
import {
  ObservabilityErrorSeverity,
  ObservabilityInit,
  reportError,
} from '@Services/observability/initObservability.ts';
import { flowIdentifierService } from '@Services/init/flow-identifier-service/flowIdentifierService.ts';

const _storeQueryParams = (): void => {
  const setQueryParamValue = useBoundStore.getState().onboardingSlice.setQueryParamValue;
  const qParamsObject = getQueryParamsObject();
  if (isObjectEmpty(qParamsObject)) return;
  if (qParamsObject[QueryParamsToStore.TOKEN]) {
    setQueryParamValue('partnerToken', qParamsObject[QueryParamsToStore.TOKEN]);
  }
  if (qParamsObject[QueryParamsToStore.REFERRAL]) {
    setQueryParamValue('referralCode', qParamsObject[QueryParamsToStore.REFERRAL]);
  }
  if (qParamsObject[QueryParamsToStore.CUSTOMER]) {
    setQueryParamValue('customerKey', qParamsObject[QueryParamsToStore.CUSTOMER]);
  }
  if (qParamsObject[QueryParamsToStore.SOURCE]) {
    setQueryParamValue('sourcePlatform', qParamsObject[QueryParamsToStore.SOURCE]);
  }
  if (
    qParamsObject[QueryParamsToStore.CUSTOMER_ID] ||
    qParamsObject[QueryParamsToStore.CUSTOMER_ID_V2]
  ) {
    setQueryParamValue(
      'customerId',
      qParamsObject[QueryParamsToStore.CUSTOMER_ID] ||
        qParamsObject[QueryParamsToStore.CUSTOMER_ID_V2]
    );
  }
  if (qParamsObject[QueryParamsToStore.AUTH_ID]) {
    setQueryParamValue('authorizedUserId', qParamsObject[QueryParamsToStore.AUTH_ID]);
  }
};

const _generateCookieObject = (cookieList: string[]) => {
  const cookieObject: Record<string, string> = {};
  const cookiesToIgnore = ['convert_experiment_data'];
  cookieList.forEach((cookie: string) => {
    if (
      cookiePrefixesToPickUp.some((prefix: string) => {
        return cookie.toLowerCase().startsWith(prefix.toLowerCase());
      })
    ) {
      let cName = cookie.split('=')[0];
      const cValue = cookie.split('=')[1];

      if (cName === '_ga') {
        cName = 'CID';
      } else if (cName === 'convert_experiment_data') {
        try {
          const convertData = JSON.parse(cValue);
          for (const key in convertData) {
            const modKey = `LP_${key}`;
            cookieObject[modKey] = convertData[key];
          }
        } catch (e) {
          console.error(e);
          const errorToReport = {
            metadata: {
              name: '_generateCookieObject failed',
              message: JSON.stringify(e),
            },
            severity: ObservabilityErrorSeverity.ERROR,
          };
          reportError(errorToReport);
        }
      }
      if ('convert' in window) {
        try {
          // eslint-disable-next-line no-unsafe-optional-chaining, @typescript-eslint/no-explicit-any
          const { experiments } = (<any>window)?.convert?.currentData;
          if (experiments) {
            const expId = Object.keys(experiments)[0];
            cookieObject['OB_experiment_id'] = expId;
            cookieObject['OB_variation_id'] = experiments[expId].variation_id;
            cookieObject['OB_variation_name'] = experiments[expId].variation_name;
          }
        } catch (e) {
          console.error(e);
          const errorToReport = {
            metadata: {
              name: 'convert failed',
              message: String(e),
            },
            severity: ObservabilityErrorSeverity.ERROR,
          };
          reportError(errorToReport);
        }
      }

      if (cName && cValue && !cookiesToIgnore.includes(cName)) {
        cookieObject[cName] = cValue;
      }
    }
  });

  return cookieObject;
};

const _buildAdSource = (): void => {
  const setQueryParamValue = useBoundStore.getState().onboardingSlice.setQueryParamValue;
  /** Get cookie list from document */
  const cookieList = getCookiesList();
  /** Convert and filter needed cookies to an Object */
  const readyCookieObject = _generateCookieObject(cookieList);
  /** Get query params */
  const qParams = getQueryParamsObject();
  /** Combine qParams object with Cookie adSource while taking priority from qParams */
  let adSource: Record<string, string> = {};
  adSource = { ...readyCookieObject, ...qParams };

  /** If adSource is empty (no cookie & no query params) */
  if (isObjectEmpty(adSource)) {
    try {
      /** Check if there is prev adSource stored in localStorage */
      const adSourceFromStorage = window.localStorage.getItem('adSource');
      setQueryParamValue('adSource', JSON.stringify(adSourceFromStorage));
    } catch (e) {
      console.error(e);
    }
  } else {
    /** Delete invalid parameter keys */
    for (const key in adSource) {
      if (adSource[key].includes('{{') || adSource[key].includes('token')) {
        delete adSource[key];
      }
    }

    /** Declare "af_adid" as afuserid */
    if (adSource['afuserid']) {
      adSource['af_adid'] = adSource['afuserid'];
    }

    /** Save adSource to local storage */
    window.localStorage.setItem('adSource', JSON.stringify(adSource));

    /** Save adSource to local storage */
    setQueryParamValue('adSource', JSON.stringify(adSource));
  }
};

const _injectFingerPrint = (): void => {
  const script = document.createElement('script');
  script.async = true;
  script.innerHTML = `import('https://fpjscdn.net/v3/${getEnvironmentVariable(EnvironmentVariables.VITE_FINGER_PRINT)}')
        .then(FingerprintJS => FingerprintJS.load())
        .then(fp => fp.get())
        .then(fpResult => {
          window.fpId = fpResult.visitorId
        })`;
  document.head.appendChild(script);
};

const _listenForInputFocus = (): void => {
  const { setFormFieldError } = useBoundStore.getState().errorSlice;

  window.addEventListener('focusin', (e): void => {
    /** Will remove the inline error on a filed that returned from the server */
    const elementId = (e.target as HTMLElement)?.id;
    if (!elementId) return;
    setFormFieldError(elementId as keyof typeof OnboardingFormFields, '');
  });
};

const _initFormSubmitOnEnter = (): void => {
  window.addEventListener('keyup', (e) => {
    if (e.key === 'Enter') {
      const activeElement = document.activeElement;
      if (activeElement && activeElement.tagName === 'INPUT') {
        (activeElement as HTMLElement)?.blur();
      }
      setTimeout(() => {
        const submitButton = document.getElementById(SUBMIT_BUTTON_ID);
        if (submitButton) {
          submitButton.click();
        }
      }, 50);
    }
  });
};

export const generateCaptchaToken = () =>
  new Promise((resolve, reject) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (<any>window).grecaptcha.ready(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (<any>window).grecaptcha
        .execute(getEnvironmentVariable(EnvironmentVariables.VITE_GRECAPTCHA), { action: 'login' })
        .catch(() => {
          reject(new Error('Captcha Token Failed'));
          const errorToReport = {
            metadata: {
              name: 'Captcha failed',
              message: 'Captcha failed',
            },
            severity: ObservabilityErrorSeverity.ERROR,
          };
          reportError(errorToReport);
        })
        .then((token: string) => {
          window.sessionStorage.setItem('captchaToken', token);
          resolve('');
        });
    });
  });

export const initSyncProcess = () => {
  const qParamsObject = getQueryParamsObject();
  const hasToken = qParamsObject[QueryParamsToStore.TOKEN] !== undefined;
  applyColorScheme(hasToken ? dunAndBradstreetColorScheme : liliColorScheme);
  _storeQueryParams();
  _buildAdSource();
  _injectFingerPrint();
  initTrackingService();
  initHotJar();
  _listenForInputFocus();
  _initFormSubmitOnEnter();
  ObservabilityInit();
};

export const initAsyncProcess = async (): Promise<void> => {
  const onboardingData = useBoundStore.getState().onboardingSlice.getOnboardingData();
  await flowIdentifierService({
    partnerToken: onboardingData.partnerToken,
    referralCode: onboardingData.referralCode,
    customerKey: onboardingData.customerKey,
    sourcePlatform: onboardingData.sourcePlatform,
    customerId: onboardingData.customerId,
    authorizedUserId: onboardingData.authorizedUserId,
  });
};
